import { CommonModule } from '@angular/common';
import { Component, computed, inject, Type } from '@angular/core';
import { NotificationBaseComponent } from './notification-base/notification-base.component';
import { ILessonListItem } from '@lb/api/mylb/interfaces';
import { MDAlertService, MDDialogService, MDDropDownComponent } from '@melodic-ui/components';
import { ConfirmationDialogComponent } from '@lb/components/confirmation-dialog/confirmation-dialog.component';
import { AttendanceService } from '@lb/api/mylb/services/lesson/attendance.service';
import { take } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { LessonMetadataService } from '@lb/services/lesson-metadata.service';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { Router } from '@angular/router';
import { usesLessonDetailsV2 } from '@lb/functions';
import { isLessonEditable } from '@lb/components/lessons/utils/lesson-helper.util';
import { TzDatePipe, TzTimePipe } from '@lb/pipes/timezone-aware-date.pipe';

@Component({
	standalone: true,
	imports: [CommonModule, NotificationBaseComponent, MDDropDownComponent, ReactiveFormsModule, TzDatePipe, TzTimePipe],
	selector: 'lb-notification-missing-attendance',
	template: `
		<lb-notification-base [notification]="notification()">
			<div activity-message>
				{{ enableLessonDetails() ? 'Lesson completion' : 'Attendance' }} is pending for the lesson on
				<br />
				<b
					>{{ lesson()?.lessonDate | tzDate: 'MMM d' }}, {{ lesson()?.lessonDate | tzTime: 'hh:mma' }} -
					{{ lesson()?.lessonEndDate | tzTime: 'hh:mma' }}</b
				>
				<br />
				<div activity-action>
					@if (!enableLessonDetails() && canEdit()) {
						<md-drop-down
							style="width: 125px;"
							[formControl]="attendanceFormControl()"
							[options]="attendanceStatusOptions()"
							(change)="updateAttendance($event)"
							placeholder="Attendance"
							md-form-input
							md-select-input
						></md-drop-down>
					} @else {
						<button md-button (click)="openDetails()">View Details</button>
					}
				</div>
			</div>
		</lb-notification-base>
	`,
	styleUrls: ['./notification-base-shared.component.scss']
})
export class NotificationMissingAttendanceComponent extends NotificationBaseComponent {
	private _lessonMetadataService = inject(LessonMetadataService);
	private _dialogService = inject(MDDialogService);
	private _attendanceService = inject(AttendanceService);
	private _alertService: MDAlertService = inject(MDAlertService);
	private _router = inject(Router);
	private _appSettings = inject<IAppSettings>(APP_CONFIG);

	// Hydrated by Notification.Api from Lesson.Api (LessonListItem shape).
	lesson = computed(() => this.notification().data as ILessonListItem);
	// The sweep addresses these to the lesson's own era instructor, so this is a guard, not the common
	// path: a viewer who may not record the attendance gets View Details instead of the inline dropdown.
	canEdit = computed(() => isLessonEditable(this.lesson()));

	// Per-lesson v2 eligibility; a failed hydration (null lesson) degrades to the legacy attendance dropdown.
	enableLessonDetails = computed(() => usesLessonDetailsV2(this.lesson(), this._appSettings));
	attendanceStatusOptions = computed(() => {
		return this._lessonMetadataService.getAttendanceStatusOptions(this.lesson()?.programID)();
	});

	attendanceFormControl = computed(() => new FormControl(this.lesson()?.attendanceStatusID));

	openDetails(): void {
		const lesson = this.lesson();

		if (!lesson) {
			return;
		}

		this._router.navigate(['/enrollments', lesson.enrollmentID, 'lessons', lesson.lessonID]);
	}

	updateAttendance(status: unknown): void {
		const lesson = this.lesson();
		if (!status) {
			return;
		}

		const dialogRef = this._dialogService.open(ConfirmationDialogComponent as Type<Component>, {
			title: 'Confirm Attendance',
			body: 'Please confirm the attendance for this lesson. This confirmation keeps an accurate record of absences and completed lessons and is required to process instructor payments and other internal workflows. Once you confirm, it cannot be undone.',
			icon: 'untitled-ui/general/check-done-02',
			successText: 'Confirm'
		});

		dialogRef.afterClosed.subscribe({
			next: (confirmed) => {
				if (confirmed) {
					this._attendanceService
						.setAttendance(lesson.lessonID, +status)
						.pipe(take(1))
						.subscribe({
							next: () => {
								this.reloadNotifications();

								this._alertService.addAlert({
									type: 'success',
									title: 'Saved Successfully!',
									message: 'The attendance was saved successfully, and notification cleared',
									duration: 3000
								});
							},
							error: () =>
								this._alertService.addAlert({
									type: 'error',
									title: 'An Error Occurred!',
									message: 'Something went wrong setting the attendance, please try again.',
									duration: 3000
								})
						});
				}
			}
		});
	}
}
